import AjaxUtils from "../util/ajax_utils";
import DataObject from "./data-object";
import EventEmitter from "../util/event-emitter";

export default class Store extends EventEmitter {
    constructor(options = undefined) {
        super(options);
        this.options = options || {};

        if (this.options.page && this.options.limit) {
            this.options.start =
                this.options.page * this.options.limit - this.options.limit;
        }

        // apply options
        Object.assign(this, this.options);
        // default this to 0 but allow to customise this value
        if (this.pageResetValue === undefined) {
            this.pageResetValue = 0;
        }
        if (!this.requestOptsFunc) {
            this.requestOptsFunc = (opts) => (opts);
        }

        if (this.options.autoLoad === true) {
            this.load({
                start: this.start,
                limit: this.limit,
                page: this.getPage(),
                sort: this.sort,
                dir: this.dir,
            });
        } else {
            // Set defaults into `lastParams` even if we skip autoLoad. Otherwise future loads will be missing these
            this.lastParams = {
                start: this.start,
                limit: this.limit,
                page: this.getPage(),
                sort: this.sort,
                dir: this.dir,
            };
        }
    }

    /**
     * The current in view data buffer
     * @type {DataObject[]}
     * @private
     */
    backingData = null;

    /**
     * A reference to the data stored within this store
     * @type {DataObject[]}
     * @private
     */
    data = null;

    /**
     * The id property of the data source used to map to DataObject and in indexing
     * @cfg {String} [idProp] defaults to 'id'
     */
    idProp = "id";

    /**
     * Allow this store to auto write modification to the DataObject's contained within
     * @cfg {Boolean}
     */
    autoWrite = false;

    /**
     * Internal property used to track the loading state of this store
     * @type {Boolean}
     */
    loading = false;

    /**
     * Source sort direction
     * @cfg {String}
     * - 'ASC'
     * - 'DESC'
     */
    dir = null;

    /**
     * The source sort parameter / field name
     * @cfg {String}
     */
    sort = null;

    /**
     * The last parameter sent to the server
     * @type {Object}
     */
    lastParams = {};

    /**
     * An internal property of the total records in the data source
     * @type {Number}
     */
    total = 0;

    /**
     * Internal property for the number of records currently loaded into this Store
     * @type {Number}
     */
    results = 0;

    /**
     * Internal property to track the current 'page' in the data source
     * @type {Number}
     */
    page = 0;

    /**
     * Internal property to keep track of the number of pages in the data source
     * @type {Number}
     */
    pages = 0;

    /**
     * The starting index of the records to pull from the data source
     * @type {Number}
     */
    start = 0;

    /**
     * The maximum number of records to pull from the data source per page
     * @type {Number}
     */
    limit = 0;

    /**
     * The index of the first record currently loaded into the Store, within the complete enumerated data set
     * @type {Number}
     */
    recStart = 0;

    /**
     * The index of the last record currently loaded into the Store, within the complete enumerated data set
     * @type {Number}
     */
    recEnd = 0;

    /**
     * Internal property that keep an index of all the DataObject loaded into this Store
     * @type {Object}
     * @private
     */
    index = {};

    /**
     * Url to the server data source
     * @cfg {String}
     */
    url = null;

    /**
     * A copy of the raw response received from the remote data source
     * @type {Object[]}
     * @private
     */
    raw = null;

    /**
     * The return type of data expected from a remote source
     * @cfg {String}
     */
    returnType = "json";

    /**
     * The default request method
     * @cfg {String}
     */
    method = "post";

    /**
     * Keeps track of the autoreload interval so that it can be cancelled
     * @private
     */
    autoreloadInterval = null;

    /**
     * Returns true if autoreload interval is currently running
     * @return Boolean
     */
    get autoreloadRunning() {
        return this.autoreloadInterval !== null;
    }

    /**
     * A callback to fire on all load() operations
     * @private
     */
    callback = () => {};

    /**
     * Trigger a load from the source server
     *
     * @fires beforeload
     * @fires beforeautoreload
     * @fires load
     * @fires autoreload
     * @fires afterload
     * @fires afterautoreload
     * @fires errorload
     * @fires errorautoreload
     * @fires showerrorflash
     *
     * @param params {Object}
     * @param callback {Function} Callback runs when data is loaded or the load fails
     * @param autoreloadEvents {Boolean} Fire autoreload specific events instead of regular load events
     * @return {void}
     */
    load(params = {}, callback = undefined, autoreloadEvents = false, noLastParams = false) {

        const Callback = callback === undefined ? this.callback : callback;
        if (!this.loading) {
            if (autoreloadEvents) {
                /**
                 * @event beforeautoreload
                 * Fired before the store loads data from a remote source
                 * @param {Store} me Reference to the Store that triggered the event
                 * @param {Object} params The parameters that the store load was triggered with that are to be sent to the server
                 */
                this.trigger("beforeautoreload", [this, params]);
            } else {
                /**
                 * @event beforeload
                 * Fired before the store loads data from a remote source
                 * @param {Store} me Reference to the Store that triggered the event
                 * @param {Object} params The parameters that the store load was triggered with that are to be sent to the server
                 */
                this.trigger("beforeload", [this, params]);
            }
            this.loading = true;
        }
        const newParams = noLastParams ? { ...params } : { ...this.lastParams, ...params };
        if (this.usePageSize && !!newParams?.limit) {
            // rename `limit` to `page_size` instead
            const page_size = newParams.limit;
            delete newParams["limit"];
            newParams.page_size = page_size;
        }
        const requestParams = {
            url: this.url,
            data: newParams,
            type: this.method,
            dataType: this.returnType,
        };

        const _this = this;
        AjaxUtils.ajax({
            ajax_opts: requestParams,
            success(data) {
                if (autoreloadEvents) {
                    /**
                     * @event autoreload
                     * Fires when the store has first loaded data
                     * @param {Store} cmp The store that loads the data
                     * @param {Object} data A copy of the raw data loaded form the source
                     */
                    _this.trigger("autoreload", [_this, data]);
                } else {
                    /**
                     * @event load
                     * Fires when the store has first loaded data
                     * @param {Store} cmp The store that loads the data
                     * @param {Object} data A copy of the raw data loaded form the source
                     */
                    _this.trigger("load", [_this, data]);
                }

                _this.raw = data;
                if (data.results !== undefined && data.results !== null) {
                    _this.total = data.results;

                    _this.updateBuffer(data.items);

                    _this.setData();

                    if (Callback && typeof Callback === "function") {
                        Callback(true, _this, _this.data);
                    }
                }
                _this.loading = false;
                if (autoreloadEvents) {
                    /**
                     * @event afterautoreload
                     * Fires when the store has finished loading
                     * @param {Store} cmp The store that trigger the event
                     * @param {Object} data A copy of the data returned from the source
                     *
                     */
                    _this.trigger("afterautoreload", [_this, _this.data]);
                } else {
                    /**
                     * @event afterload
                     * Fires when the store has finished loading
                     * @param {Store} cmp The store that trigger the event
                     * @param {Object} data A copy of the data returned from the source
                     *
                     */
                    _this.trigger("afterload", [_this, _this.data]);
                }
            },
            fail(err) {
                _this.loading = false;
                if (autoreloadEvents) {
                    /**
                     * @event errorautoreload
                     * Fires when an error has occurred when loading data
                     * @param {Store} cmp The store that triggered the event
                     * @param {Object} error The raw error object
                     */
                    _this.trigger("errorautoreload", [_this, err]);
                } else {
                    /**
                     * @event errorload
                     * Fires when an error has occurred when loading data
                     * @param {Store} cmp The store that triggered the event
                     * @param {Object} error The raw error object
                     */
                    _this.trigger("errorload", [_this, err]);
                }
                if (Callback && typeof Callback === "function") {
                    Callback(false, _this);
                }

                if (err?.success === false) {
                   /**
                     * @event showerrorflash
                     * Fires when the store encounters a response with the key 'success' set to false
                     * @param {Store} cmp The store that trigger the event
                     * @param {Object} data A copy of the data returned from the source
                     */
                    _this.trigger("showerrorflash", [_this, err]);
                }
            },
            opts: this.requestOptsFunc({
                flash_form_errors: false,
                flash_404_errors: false,
                show_success_flash: false,
                show_error_flash: false,
            }),
        });
        // Set our last params.
        this.lastParams = newParams;
    }

    /**
     * Reload a store, shortcut uses last params
     *
     * @return {void}
     */
    reload(autoreloadEvents = false) {
        this.load({...this.lastParams}, undefined, autoreloadEvents);
    }

    /**
     * Autoreload the store on a regular interval
     *
     * @param interval {Number} Interval delay in milliseconds
     * @return {void}
     */
    autoreload(interval) {
        if (this.autoreloadRunning) {
            this.stopAutoreload();
        }
        this.autoreloadInterval = setInterval(() => {
            this.reload(true);
        }, interval);
    }

    /**
     * Cancel the autoreload
     *
     * @return {void}
     */
    stopAutoreload() {
        if (this.autoreloadRunning) {
            clearInterval(this.autoreloadInterval);
            this.autoreloadInterval = null;
        }
    }

    /**
     * Move the store to the next source page of data if available
     *
     * @return {void}
     */
    next() {
        if (this.page < this.pages) {
            this.setPage(
                this.page + 1,
                this.backingData &&
                    this.backingData.length >=
                        this.limit * this.page + this.limit
            );
        }
    }

    /**
     * Move the store to the prev page of source data if available
     *
     * @return {void}
     */
    prev() {
        if (this.page > 1) {
            this.setPage(
                this.page - 1,
                this.backingData &&
                    this.backingData.length >=
                        this.limit * (this.page - 1) + this.limit
            );
        }
    }

    /**
     * Go to a page of data in the source
     *
     * @param  {Number} page
     * @return {void}
     */
    goto(page) {
        this.setPage(
            page,
            this.backingData &&
                this.backingData.length >= this.limit * page + this.limit
        );
    }

    /**
     * Jump to the first source data page
     *
     * @return {void}
     */
    first() {
        this.setPage(
            1,
            this.backingData && this.backingData.length >= this.total
        );
    }

    /**
     * Move to the last source data page if available
     *
     * @return {void}
     */
    last() {
        this.setPage(
            this.pages,
            this.backingData && this.backingData.length >= this.total
        );
    }

    /**
     * Set the data that is held by this store
     * @fires datachanged
     *
     * @param {DataObject[]} data
     */
    setData(data = undefined) {
        // TODO: Think hard about this here be dragons!!??
        if (data) {
            this.data = data;
        } else if (this.backingData) {
            if (this.backingData.length >= this.start + this.limit) {
                this.data = this.backingData.slice(
                    this.start,
                    this.start + this.limit
                );
            } else {
                this.data = this.backingData.slice(0, this.limit);
            }
            this.results = this.data ? this.data.length : 0;
        }
        /**
         * @event datachanged
         * The data changed event is fired everytime the internal data of this store changes
         * @param {Store} store A copy of the store that fired the event
         * @param {Object[]} data A copy of the stores current data
         */
        this.trigger("datachanged", [this, this.data]);
    }

    /**
     * @return {Object} data
     */
    getData() {
        return this.data;
    }

    /**
     * Parses records in `data`, adds DataObject representations to the `backingData` buffer and index
     * @param  {Object} data
     * @param  {Object} config
     * @return {void}
     * @private
     */
    updateBuffer(data, config = {}) {
        const defaults = {
            dirty: false,
            append: false,
        };
        // Using an object for this so more options can be passed in later...
        const options = {...defaults, ...config};

        // Only really care about this when doing normal buffer updates...
        if (!options.append) {
            this.backingData = [];
        }
        for (let i = 0, l = data.length; i < l; i++) {
            const rec = data[i];
            if (rec instanceof DataObject) {
                if (rec.store !== this) {
                    rec.store = this;
                }
                this.backingData.push(rec);
            } else {
                let DO;
                if (rec[this.idProp]) {
                    DO = new DataObject(rec, {
                        id: rec[this.idProp],
                        store: this,
                        autoWrite: this.autoWrite,
                    });
                } else {
                    DO = new DataObject(rec, {
                        store: this,
                        autoWrite: this.autoWrite,
                    });
                }
                this.setIndex(DO.id, DO);
                this.backingData.push(DO);
            }
        }

        this.results = data.length <= this.limit ? data.length : this.limit;

        this.recStart = this.total > 0 ? this.start + 1 : 0;
        this.recEnd =
            this.recStart + this.limit - 1 < this.total
                ? this.recStart + this.limit - 1
                : this.total;

        this.updatePagination(true);
    }

    /**
     * Index an DataObject in this store
     *
     * @param {String} id
     * @param {DataObject} record
     */
    setIndex(id, record) {
        this.index[id] = record;
    }

    /**
     * Looks up and returns an indexed DataObject
     *
     * @param  {String} id
     * @return {DataObject}
     */
    getIndex(id) {
        return this.index[id];
    }

    /**
     * Cleans out the index
     *
     * @return {void}
     */
    purgeIndex() {
        this.index = {};
    }

    /**
     * @param {Number|string} page
     */
    setterPage(page) {
        const pageNum = parseInt(page, 10);
        this.page = Number.isInteger(pageNum) && pageNum > 0 ? pageNum : 1;

        if (this.backingData && this.page > this.pages) {
            this.page = this.pages;
        }

        // Do not show page 0
        if (this.page < 1) {
            this.page = 1;
        }

        this.start = Math.max(0, this.page * this.limit - this.limit);

        this.recStart = this.total > 0 ? this.start + 1 : 0;
        this.recEnd =
            this.recStart + this.limit - 1 < this.total
                ? this.recStart + this.limit - 1
                : this.total;
    }

    /**
     * @param {Number|string} page
     * @param {Boolean} [local]
     */
    setPage(page, local) {
        this.setterPage(page);
        // TODO: use backing data as a buffer?????
        // IE we can continue to load more data in when paging / behind the scenes...????
        if (local) {
            this.setData();
        } else {
            this.load({
                start: this.start,
                limit: this.limit,
                page: this.getPage(),
                sort: this.sort,
                dir: this.dir,
            }, this.callback);
        }
        this.updatePagination(false);
    }

    /**
     * @return {Number} page
     */
    getPage() {
        return this.page;
    }

    /**
     * Updates the pagination based upon the current source records and total loaded DataObject's
     *
     * @return {void}
     */
    updatePagination(isUpdateBuffer) {
        this.pages = Math.ceil(this.total / this.limit);
        this.page = Math.floor(this.start / this.limit) + 1;
        const length = this.backingData ? this.backingData.length : 1;
        if (this.page < 0 || length < 1) {
            // This is a bit of a hack, this.start shouldn't ever get set to a negative number (but it is, so restore sanity).
            this.start = 0;
            this.page = this.pageResetValue;
            this.pages = 0;
        }
        this.trigger('updatepagination', [this, isUpdateBuffer]);
    }

    /**
     * Setter for source record limit
     * @param {Number} limit Integer above 0
     * @param {Boolean} local Set data from the local buffer, without making another remote store call
     */
    setLimit(limit, local = false) {
        const limitNum = Math.max(1, parseInt(limit, 10));
        if (Number.isInteger(limitNum)) {
            this.limit = limitNum;
        } else {
            throw TypeError("Limit is not a valid positive number");
        }

        if (local) {
            this.setData();
        } else {
            this.load({
                start: this.start,
                limit: this.limit,
                page: this.getPage(),
                sort: this.sort,
                dir: this.dir,
            });
        }
        this.trigger('updatepagesize', [this]);
    }
}
